/**
 * Connection Manager
 * Handles organization database connections and client caching
 */

import { SupabaseClient } from "@supabase/supabase-js";
import { RegistryClient } from "../registryClient";
import { OrganizationClient } from "../organizationClient";
import { STORAGE_KEYS, RETRY_CONFIG } from "@/services/supabase/config";
import {
  cacheOrganizationCredentials,
  getCachedOrganizationCredentials,
} from "../utils/credentialsCache";

/**
 * Manages database connections with three-level caching:
 * 1. Current active connection
 * 2. In-memory Map cache
 * 3. localStorage persistence
 */
export class ConnectionManager {
  private registryClient: RegistryClient;
  private currentClient: SupabaseClient | null = null;
  private currentOrganizationSlug: string | null = null;
  private organizationClients: Map<string, OrganizationClient> = new Map();

  constructor() {
    this.registryClient = new RegistryClient();
  }

  getRegistryClient(): SupabaseClient {
    return this.registryClient.getClient();
  }

  getCurrentClient(): SupabaseClient {
    if (!this.currentClient) {
      throw new Error("No organization client connected. Please connect to an organization first.");
    }
    return this.currentClient;
  }

  getCurrentOrganizationSlug(): string | null {
    if (this.currentOrganizationSlug) {
      return this.currentOrganizationSlug;
    }
    
    const storedSlug = localStorage.getItem(STORAGE_KEYS.currentOrganizationSlug);
    if (storedSlug) {
      this.currentOrganizationSlug = storedSlug;
      return storedSlug;
    }
    
    return null;
  }

  async connectToOrganizationBySlug(slug: string): Promise<SupabaseClient> {
    try {
      if (this.currentOrganizationSlug === slug && this.currentClient) {
        this.persistCurrentOrganization(slug);
        return this.currentClient;
      }

      if (this.organizationClients.has(slug)) {
        return this.connectFromCache(slug);
      }

      return await this.createNewConnection(slug);
    } catch (error) {
      console.error(`❌ Error connecting to organization ${slug}:`, error);
      throw error;
    }
  }

  async connectToStoredOrganization(email: string): Promise<SupabaseClient | null> {
    try {
      const storedSlug = localStorage.getItem(STORAGE_KEYS.currentOrganizationSlug);
      
      if (!storedSlug) {
        return null;
      }

      try {
        return await this.connectToOrganizationBySlug(storedSlug);
      } catch (error) {
        console.error("Error connecting to stored organization:", error);
        return null;
      }
    } catch (error) {
      console.error("Error in connectToStoredOrganization:", error);
      return null;
    }
  }

  cacheOrganizationClient(slug: string, client: OrganizationClient): void {
    this.organizationClients.set(slug, client);
  }

  disconnect(): void {
    this.currentClient = null;
    this.currentOrganizationSlug = null;
  }

  async resolveOrganizationSupabaseUrl(slug: string): Promise<string | null> {
    const org = await this.getOrganizationWithRetry(slug);
    return org?.supabaseUrl ?? null;
  }

  private async getOrganizationWithRetry(slug: string) {
    let orgRegistry = null;
    let attempts = 0;
    const maxAttempts = RETRY_CONFIG.maxAttempts;

    while (!orgRegistry && attempts < maxAttempts) {
      try {
        attempts++;
        orgRegistry = await this.registryClient.getOrganizationBySlug(slug);
        
        if (!orgRegistry && attempts < maxAttempts) {
          await this.delay(RETRY_CONFIG.initialDelay);
        }
      } catch (error) {
        console.error(`Error getting organization registry (attempt ${attempts}):`, error);
        if (attempts < maxAttempts) {
          await this.delay(RETRY_CONFIG.initialDelay);
        }
      }
    }

    if (!orgRegistry) {
      console.error(`Organization '${slug}' not found after ${maxAttempts} attempts`);
    }

    return orgRegistry;
  }

  private connectFromCache(slug: string): SupabaseClient {
    const orgClient = this.organizationClients.get(slug)!;
    this.currentClient = orgClient.getClient();
    this.currentOrganizationSlug = slug;
    this.persistCurrentOrganization(slug);
    return this.currentClient;
  }

  private async createNewConnection(slug: string): Promise<SupabaseClient> {
    const orgRegistry = await this.getOrganizationWithRetry(slug);
    if (!orgRegistry) {
      // Registry unreachable (typically offline). Credentials cached from a
      // previous successful connection let the org client be rebuilt locally,
      // so an offline cold start can still reach the stored auth session and
      // the persisted query cache. Connection validation is skipped: it needs
      // the network, and these credentials already validated once.
      const cached = getCachedOrganizationCredentials(slug);
      if (cached) {
        console.warn(
          `Registry unavailable for '${slug}'; connecting with locally cached credentials`
        );
        return this.activateConnection(
          slug,
          new OrganizationClient(cached.supabaseUrl, cached.supabaseAnonKey)
        );
      }
      throw new Error(`Organization with slug '${slug}' not found in registry`);
    }

    const orgClient = new OrganizationClient(
      orgRegistry.supabaseUrl,
      orgRegistry.supabaseAnonKey,
    );

    await orgClient.validateConnection();

    // Persist only after validation so offline fallbacks never reuse
    // credentials that were rotated or wrong.
    cacheOrganizationCredentials(slug, {
      supabaseUrl: orgRegistry.supabaseUrl,
      supabaseAnonKey: orgRegistry.supabaseAnonKey,
    });

    return this.activateConnection(slug, orgClient);
  }

  private activateConnection(slug: string, orgClient: OrganizationClient): SupabaseClient {
    this.organizationClients.set(slug, orgClient);
    this.currentClient = orgClient.getClient();
    this.currentOrganizationSlug = slug;
    this.persistCurrentOrganization(slug);
    return this.currentClient;
  }

  private persistCurrentOrganization(slug: string): void {
    localStorage.setItem(STORAGE_KEYS.currentOrganizationSlug, slug);
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
